import React, { useState } from 'react';
import { addToWaitlist } from '../services/authStore';

const LeftSidebar = ({ currentUser, referralCode, onClose }) => {
  const [expandedWidget, setExpandedWidget] = useState(['pain', 'referral']); // Open both by default
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [email, setEmail] = useState('');
  const [painPoints, setPainPoints] = useState({
    'Burnout': false,
    'RSI/Pain': false,
    'Sleep Issues': false,
    'Low Energy': false,
    'Focus Problems': false,
    'Weight/Fitness': false
  });
  const [waitlistMessage, setWaitlistMessage] = useState('');
  const [waitlistLoading, setWaitlistLoading] = useState(false);

  const toggleWidget = (widgetName) => {
    setExpandedWidget(prev => 
      prev.includes(widgetName) 
        ? prev.filter(w => w !== widgetName)
        : [...prev, widgetName]
    );
  };

  const handlePainPointChange = (point) => {
    setPainPoints(prev => ({ ...prev, [point]: !prev[point] }));
  };

  const handleWaitlistSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim()) {
      setWaitlistMessage('Please enter your email');
      return;
    }

    setWaitlistLoading(true);
    setWaitlistMessage('');

    const selectedPainPoints = Object.entries(painPoints)
      .filter(([_, checked]) => checked)
      .map(([point, _]) => point);

    const result = await addToWaitlist({
      email: email.trim(),
      painPoints: selectedPainPoints.join(', ') || 'None selected'
    });

    setWaitlistLoading(false);

    if (result.ok) {
      setWaitlistMessage(`✅ You're #${result.position} on the waitlist!`);
      setEmail('');
      setPainPoints({
        'Burnout': false,
        'RSI/Pain': false,
        'Sleep Issues': false,
        'Low Energy': false,
        'Focus Problems': false,
        'Weight/Fitness': false
      });
    } else {
      setWaitlistMessage(`❌ ${result.error}`);
    }
  };

  if (isCollapsed) {
    return (
      <div className="w-12 flex-shrink-0 h-full bg-[#0a0a0a] border-r border-white/10 flex flex-col items-center py-4 gap-4 transition-all duration-300">
        <button
          onClick={() => setIsCollapsed(false)}
          className="w-8 h-8 rounded bg-white/10 hover:bg-white/20 flex items-center justify-center transition"
          title="Expand sidebar"
        >
          <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <polyline points="9 18 15 12 9 6" />
          </svg>
        </button>
        <button
          onClick={() => { setIsCollapsed(false); setExpandedWidget(['pain']); }}
          className="w-8 h-8 rounded bg-white/10 hover:bg-green-btn/50 flex items-center justify-center transition text-xs"
          title="Pain Points"
        >
          📋
        </button>
        <button
          onClick={() => { setIsCollapsed(false); setExpandedWidget(['referral']); }}
          className="w-8 h-8 rounded bg-white/10 hover:bg-green-btn/50 flex items-center justify-center transition text-xs"
          title="Referrals"
        >
          🎁
        </button>
      </div>
    );
  }

  return (
    <div className="w-64 flex-shrink-0 h-full bg-[#0a0a0a] border-r border-white/10 overflow-y-auto flex flex-col transition-all duration-300 max-h-[calc(100vh-64px)]">
      {/* Collapse button */}
      <div className="p-3 border-b border-white/10 flex justify-between items-center">
        <span className="text-xs font-bold text-gray-400 uppercase tracking-wider">Widgets</span>
        <button
          onClick={() => {
            setIsCollapsed(true);
            if (onClose) onClose();
          }}
          className="w-6 h-6 rounded bg-white/10 hover:bg-white/20 flex items-center justify-center transition"
          title="Collapse sidebar"
        >
          <svg className="w-3 h-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <polyline points="15 18 9 12 15 6" />
          </svg>
        </button>
      </div>

      {/* Pain Points Widget */}
      <div className="border-b border-white/10">
        <button
          onClick={() => toggleWidget('pain')}
          className="w-full p-3 flex items-center justify-between hover:bg-white/5 transition text-left"
        >
          <div className="flex items-center gap-2">
            <span className="text-lg">📋</span>
            <span className="text-sm font-semibold">Pain Points</span>
          </div>
          <svg
            className={`w-4 h-4 transition-transform ${expandedWidget.includes('pain') ? 'rotate-180' : ''}`}
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
          >
            <polyline points="6 9 12 15 18 9" />
          </svg>
        </button>
        {expandedWidget.includes('pain') && (
          <div className="p-3 bg-[#0f0f0f] space-y-2">
            <p className="text-xs text-gray-400 mb-3 font-semibold">Join waitlist for exclusive news and gifts</p>
            <form onSubmit={handleWaitlistSubmit} className="space-y-2">
              <input
                type="email"
                placeholder="your@email.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full px-2 py-1.5 text-xs bg-black border border-gray-600 rounded text-white focus:outline-none focus:border-green-btn"
                disabled={waitlistLoading}
              />
              <div className="space-y-1">
                {Object.keys(painPoints).map((point) => (
                  <label key={point} className="flex items-center gap-2 text-xs cursor-pointer hover:text-green-btn">
                    <input
                      type="checkbox"
                      checked={painPoints[point]}
                      onChange={() => handlePainPointChange(point)}
                      className="rounded bg-black border-gray-600"
                      disabled={waitlistLoading}
                    />
                    <span>{point}</span>
                  </label>
                ))}
              </div>
              <button
                type="submit"
                disabled={waitlistLoading}
                className="w-full mt-3 px-3 py-2 bg-green-btn text-black text-xs font-bold rounded hover:opacity-90 disabled:opacity-50"
              >
                {waitlistLoading ? 'Joining...' : 'Join Waitlist'}
              </button>
              {waitlistMessage && (
                <p className={`text-xs text-center ${waitlistMessage.includes('✅') ? 'text-green-btn' : 'text-red-400'}`}>
                  {waitlistMessage}
                </p>
              )}
            </form>
          </div>
        )}
      </div>

      {/* Referrals Widget */}
      <div className="border-b border-white/10">
        <button
          onClick={() => toggleWidget('referral')}
          className="w-full p-3 flex items-center justify-between hover:bg-white/5 transition text-left"
        >
          <div className="flex items-center gap-2">
            <span className="text-lg">🎁</span>
            <span className="text-sm font-semibold">Early Commit</span>
          </div>
          <svg
            className={`w-4 h-4 transition-transform ${expandedWidget.includes('referral') ? 'rotate-180' : ''}`}
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
          >
            <polyline points="6 9 12 15 18 9" />
          </svg>
        </button>
        {expandedWidget.includes('referral') && (
          <div className="p-3 bg-[#0f0f0f] space-y-3">
            <p className="text-xs text-gray-400 mb-2">Unlock exclusive rewards:</p>
            <div className="space-y-2">
              <div className="bg-black/50 p-2 rounded border border-white/5">
                <div className="flex justify-between items-center mb-1">
                  <span className="text-xs font-bold text-white">Jr Developer</span>
                  <span className="text-xs text-green-btn font-bold">3 refs</span>
                </div>
                <p className="text-[10px] text-gray-400">DevVital stickers + Newsletter</p>
              </div>
              <div className="bg-black/50 p-2 rounded border border-white/5">
                <div className="flex justify-between items-center mb-1">
                  <span className="text-xs font-bold text-white">Team Lead</span>
                  <span className="text-xs text-green-btn font-bold">10 refs</span>
                </div>
                <p className="text-[10px] text-gray-400">1 month green coffee + mug or tee</p>
              </div>
              <div className="bg-black/50 p-2 rounded border border-white/5">
                <div className="flex justify-between items-center mb-1">
                  <span className="text-xs font-bold text-white">Legend</span>
                  <span className="text-xs text-green-btn font-bold">100 refs</span>
                </div>
                <p className="text-[10px] text-gray-400">Monthly green coffee for 6 months</p>
              </div>
            </div>
            <div className="pt-2 border-t border-white/10 text-xs text-gray-400">
              {currentUser && referralCode ? (
                <div className="space-y-2">
                  <p className="text-green-btn font-semibold">Your referral link:</p>
                  <div className="bg-black border border-green-btn/30 rounded p-2 text-[10px] break-all text-white">
                    {`${process.env.REACT_APP_SITE_URL || window.location.origin}/?ref=${referralCode}`}
                  </div>
                  <button
                    onClick={async () => {
                      const link = `${process.env.REACT_APP_SITE_URL || window.location.origin}/?ref=${referralCode}`;
                      try {
                        if (navigator.clipboard?.writeText) {
                          await navigator.clipboard.writeText(link);
                          alert('Link copied to clipboard!');
                        }
                      } catch (err) {
                        console.error('Copy error:', err);
                      }
                    }}
                    className="w-full px-2 py-1.5 bg-green-btn text-black text-xs font-bold rounded hover:opacity-90"
                  >
                    Copy Link
                  </button>
                </div>
              ) : (
                <p>Share your code to unlock rewards</p>
              )}
            </div>
          </div>
        )}
      </div>

      {/* About Widget */}
      <div className="border-b border-white/10">
        <button
          onClick={() => toggleWidget('about')}
          className="w-full p-3 flex items-center justify-between hover:bg-white/5 transition text-left"
        >
          <div className="flex items-center gap-2">
            <span className="text-lg">ℹ️</span>
            <span className="text-sm font-semibold">About</span>
          </div>
          <svg
            className={`w-4 h-4 transition-transform ${expandedWidget.includes('about') ? 'rotate-180' : ''}`}
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
          >
            <polyline points="6 9 12 15 18 9" />
          </svg>
        </button>
        {expandedWidget.includes('about') && (
          <div className="p-3 bg-[#0f0f0f] space-y-2 text-xs text-gray-300">
            <p>DevVital helps developers optimize their human hardware.</p>
            <p className="text-gray-500">Share experiences, discuss health strategies, and build sustainable coding habits.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default LeftSidebar;
